import type { RecursiveObject } from './types'

/**
 * Converts a flat array of key-value pairs into a nested object
 * The splitKey function is used to split the key into an array of strings that are then used for nesting deeper
 *
 * Example:
 * input = [
 * { key: 'a.b.c', value: 1 },
 * { key: 'a.b.d', value: 2 },
 * { key: 'a.e', value: 3 },
 * { key: 'f', value: 4 },
 *]
 *
 * const output = nestByKey(key => key.split('.'), input)
 *
 * Result: {
 * a: {
 *  b: {
 *    c: 1,
 *    d: 2,
 *  },
 *  e: 3,
 * },
 * f: 4,
 *}
 *
 * @param splitKey - function to split the key into an array of strings
 * @param struct - array of objects with key and value
 * @returns a nested object
 */

export const nestByKey = <V>(
  splitKey: (key: string) => string[],
  struct: Array<{ key: string; value: V }>
): RecursiveObject<V> => {
  const nest = (
    obj: RecursiveObject<V>,
    keys: string[],
    value: V
  ): RecursiveObject<V> => {
    if (keys.length === 0 || !keys) {
      return obj
    }

    const [first, ...rest] = keys

    if (rest.length === 0) {
      return { ...obj, [first]: value }
    }

    const currentValue = obj[first]

    return {
      ...obj,
      [first]:
        typeof currentValue === 'object' && currentValue !== null
          ? nest(currentValue as RecursiveObject<V>, rest, value)
          : nest({}, rest, value),
    }
  }

  if (!struct || struct.length === 0) {
    return {}
  }

  return struct.reduce((acc, { key, value }) => {
    const keys = splitKey(key)

    if (keys.length === 0) {
      return acc
    }

    return nest(acc, keys, value)
  }, {} as RecursiveObject<V>)
}
